import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@awesome.me/kit-70c1f0055f/icons/classic/light";
import { faCheck } from "@awesome.me/kit-70c1f0055f/icons/classic/solid";
import { ANI_PERSONALITY_MODES, type AniPersonalityMode } from "@/context/AniContext";
import { ANI_PERSONALITY_MODE_ICONS } from "./aniPersonalityModeIcons";
import styles from "./AniPersonalityModePicker.module.css";

export interface AniPersonalityModePickerProps {
  /** Currently selected personality mode. */
  value: AniPersonalityMode;
  /** Called when the user selects a mode. */
  onChange: (mode: AniPersonalityMode) => void;
  /** Disables the control (e.g. while sending). */
  disabled?: boolean;
}

/**
 * Compact dropdown to change Ani personality mode, for use beside the chat composer.
 */
export function AniPersonalityModePicker({ value, onChange, disabled }: AniPersonalityModePickerProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleDown = (event: MouseEvent) => {
      const el = wrapRef.current;
      if (el && !el.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleDown);
    return () => document.removeEventListener("mousedown", handleDown);
  }, [open]);

  const current = ANI_PERSONALITY_MODES.find((m) => m.id === value) ?? ANI_PERSONALITY_MODES[1];
  const currentIcon = ANI_PERSONALITY_MODE_ICONS[value];

  /** Animate only the visible fun icon: trigger when menu is closed, list row when menu is open (avoids duplicate running animations). */
  const animateFunOnTrigger = value === "fun" && !open;

  return (
    <div ref={wrapRef} className="relative" data-ani-personality-picker data-testid="ani-personality-mode-picker">
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-sm text-slate-600 hover:bg-slate-200/80 dark:text-slate-400 dark:hover:bg-slate-700/80 disabled:opacity-50 disabled:pointer-events-none"
        aria-expanded={open}
        aria-haspopup="listbox"
        title={`Ani mode: ${current.label}`}
      >
        <FontAwesomeIcon
          icon={currentIcon}
          className={`h-3.5 w-3.5 ${
            animateFunOnTrigger
              ? styles.funIconCycle
              : value === "fun"
                ? "text-purple-500 dark:text-purple-400"
                : "text-slate-500 dark:text-slate-400"
          }`}
        />
        <span className="max-w-[5.5rem] truncate">{current.label}</span>
        <FontAwesomeIcon icon={faChevronDown} className="h-2.5 w-2.5 " />
      </button>

      {open && (
        <ul
          className="absolute bottom-full left-0 z-50 mb-2 w-48 rounded-2xl overflow-hidden p-2  py-2 shadow-xl dark:bg-slate-900"
          role="listbox"
          aria-label="Ani mode"
        >
          {ANI_PERSONALITY_MODES.map((mode) => (
            <li key={mode.id} role="option" aria-selected={value === mode.id}>
              <button
                type="button"
                data-testid={`ani-personality-mode-option-${mode.id}`}
                onClick={() => {
                  onChange(mode.id);
                  setOpen(false);
                }}
                className={`flex w-full items-center rounded-xl gap-2 px-2.5 py-1.5 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-700/50 ${
                  value === mode.id ? "bg-slate-50 dark:bg-slate-700/30" : ""
                }`}
              >
                <FontAwesomeIcon
                  icon={ANI_PERSONALITY_MODE_ICONS[mode.id]}
                  className={`h-3.5 w-3.5 shrink-0 ${
                    mode.id === "fun" ? styles.funIconCycle : "text-slate-500 dark:text-slate-400"
                  }`}
                />
                <span className="min-w-0 flex-1 text-slate-900 dark:text-slate-100">{mode.label}</span>
                {value === mode.id && (
                  <FontAwesomeIcon icon={faCheck} className="h-3 w-3 shrink-0 text-white" />
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
